import { useMemo, useState } from "react";

import {
  GameCommandByPlayer,
  GameReducerFactoryProps,
  LobbyGame,
  LobbySyncAdapterProps,
  StateWithUndo,
  UndoCommand,
  UndoState,
  gameStateManagerFactory,
  useStateManager,
} from "../../infra";
import { EventBus, arrayLast, emptyFct } from "../../lib";
import { useInterval, useSessionUser } from "../hook";
import { useNotifications } from "../notification";
import { contextFactory } from "./contextFactory";

export type GameContext<Game, GameCommand> = {
  game: Game;
  undoState: UndoState;
  commands: GameCommandByPlayer<GameCommand | UndoCommand>[];
  update: (command: GameCommand | UndoCommand) => void;
  deleteGame: () => void;
  syncStatus: ReturnType<typeof useStateManager>["status"];
  stateChangeBus: EventBus<{
    game: Game;
    undoState: UndoState;
    event: GameCommandByPlayer<GameCommand | UndoCommand>;
  }>;
  isUpToDate: boolean;
  addNotification: (element: React.ReactNode) => void;
  history: StateWithUndo<Game, GameCommand>;
};

export const gameContextFactory = <Game, GameCommand>({
  useLobbyGame,
  newGame,
  syncAdapterProps,
  gameReducerFactoryProps,
  notificationDelay = 1000 * 10,
}: {
  useLobbyGame: () => LobbyGame | undefined;
  newGame: (lobbyGame: LobbyGame) => Game;
  syncAdapterProps: LobbySyncAdapterProps;
  gameReducerFactoryProps: GameReducerFactoryProps<Game, GameCommand>;
  notificationDelay?: number;
}) => {
  const sessionUser = useSessionUser();
  const {
    Provider: GameProvider,
    useCustomContext: useGame,
    ProviderRaw: GameProviderRaw,
  } = contextFactory<GameContext<Game, GameCommand>>(function gameContext() {
    const lobbyGame = useLobbyGame();
    if (!lobbyGame) {
      console.warn("no lobby game found");
      return null;
    }
    const [isUpToDate, setUpToDate] = useState(false);
    const { add: addNotificationLocal } = useNotifications();

    const addNotification = (content: React.ReactNode) => {
      if (isUpToDate) {
        addNotificationLocal({
          content,
          delayInMs: notificationDelay,
        });
      }
    };

    const { pollingSync } = useMemo(() => {
      return gameStateManagerFactory({
        gameId: lobbyGame.id,
        playerIds: lobbyGame.users.map((user) => user.id),
        initGame: newGame(lobbyGame),
        gameReducerFactoryProps,
        syncAdapterProps,
      });
    }, []);
    useInterval(
      () => {
        if (!isUpToDate && pollingSync.isUpToDate) {
          setUpToDate(true);
        }
      },
      1000,
      [pollingSync, isUpToDate]
    );
    const stateChangeBus = new EventBus<{
      game: Game;
      undoState: UndoState;
      event: GameCommandByPlayer<GameCommand | UndoCommand>;
    }>();

    const { status: syncStatus } = useStateManager(
      pollingSync,
      (state, event) => {
        const lastState = arrayLast(state.steps)?.state ?? state.initState;
        stateChangeBus.send({
          game: lastState.gameState,
          event,
          undoState: lastState.undoState,
        });
      },
      isUpToDate
    );
    const history = pollingSync.state;
    const { steps, initState } = history;
    const lastState = arrayLast(steps)?.state ?? initState;
    const { gameState: game, undoState } = lastState;
    const commands = steps.map(({ command }) => command);

    async function update(command: GameCommand | UndoCommand) {
      const commandByPlayer = { ...command, playerId: sessionUser.id };
      pollingSync.send(commandByPlayer);
    }

    const deleteGame = async () => {
      await pollingSync.delete();
    };
    return {
      game,
      undoState,
      update,
      deleteGame,
      commands,
      syncStatus,
      stateChangeBus,
      isUpToDate,
      addNotification,
      history,
    };
  });
  const useGameOpt = () => {
    try {
      const result = useGame();
      return result;
    } catch (err) {
      return null;
    }
  };

  const { useCustomContext: useReplayContext, Provider: ReplayProviderLocal } =
    contextFactory(function replayContext() {
      const lobbyGame = useLobbyGame();
      if (!lobbyGame) throw new Error("unexpected: no lobby game");
      const [commandIndex, setCommandIndex] = useState(0);
      const { commands, history } = useGame();
      const undoAndGameState =
        commandIndex === 0
          ? history.initState
          : history.steps.at(commandIndex - 1)?.state;
      if (!undoAndGameState) {
        throw new Error(`could not find step at index ${commandIndex - 1}`);
      }
      const { gameState: game, undoState } = undoAndGameState;

      return {
        commandIndex,
        setCommandIndex,
        commands,
        game,
        undoState,
        history,
      };
    });

  const GameContextBuilder = ({
    children,
  }: {
    children: React.ReactElement;
  }) => {
    const { commands, history } = useGame();
    const { game, commandIndex, undoState } = useReplayContext();
    return (
      <GameProviderRaw
        value={{
          game,
          undoState,
          update: emptyFct,
          deleteGame: emptyFct,
          commands: commands.slice(0, commandIndex),
          syncStatus: "synced",
          stateChangeBus: new EventBus(),
          isUpToDate: true,
          addNotification: emptyFct,
          history: {
            ...history,
            steps: history.steps.slice(0, commandIndex),
          },
        }}
      >
        {children}
      </GameProviderRaw>
    );
  };

  const GameReplayProvider = ({
    children,
  }: {
    children: React.ReactElement;
  }) => {
    return (
      <ReplayProviderLocal>
        <GameContextBuilder>{children}</GameContextBuilder>
      </ReplayProviderLocal>
    );
  };
  return {
    GameProvider,
    useGame,
    useGameOpt,
    GameReplayProvider,
    useReplayContext,
  };
};
